import React, {useCallback, useState, useMemo} from 'react';

import {assertRoomID} from '@pexip/infinity';

import type {BreakoutParticipants, BreakoutRoomNames, RoomID} from '../types';
import {isDuplicateRoomName} from '../utils/isDuplicateRoomName';
import {getBreakoutRooms} from '../utils/getBreakoutRooms';
import {DuplicateRoomNameModal} from '../views/DuplicateRoomNameModal/DuplicateRoomNameModal.view';

export const useBreakoutRoomNameChange = ({
    participants,
    setParticipants,
    breakoutRoomsNames,
    setBreakoutRoomsNames,
}: {
    participants: BreakoutParticipants;
    setParticipants: React.Dispatch<React.SetStateAction<BreakoutParticipants>>;
    breakoutRoomsNames?: BreakoutRoomNames;
    setBreakoutRoomsNames?: React.Dispatch<
        React.SetStateAction<BreakoutRoomNames>
    >;
}) => {
    const [showDuplicateRoomNameModal, setShowDuplicateRoomNameModal] =
        useState(false);
    const handleBreakoutRoomNameChange = useCallback(
        (targetId: RoomID, newName: string) => {
            if (
                isDuplicateRoomName(newName, [
                    ...getBreakoutRooms(participants),
                    ...(breakoutRoomsNames
                        ? Object.values(breakoutRoomsNames)
                        : []),
                ])
            ) {
                setShowDuplicateRoomNameModal(true);
                return false;
            }

            if (
                breakoutRoomsNames &&
                setBreakoutRoomsNames &&
                breakoutRoomsNames.has(targetId)
            ) {
                // renaming existing room
                setBreakoutRoomsNames({
                    ...breakoutRoomsNames,
                    [targetId]: newName,
                });
            } else {
                // renaming a new room
                setParticipants(participants => {
                    const participantList = participants.get(targetId);
                    if (participantList) {
                        assertRoomID(newName);
                        participants.set(newName, participantList);
                        participants.delete(targetId);
                        // Need a new Map to make React happy
                        return new Map(participants);
                    }
                    return participants;
                });
            }
            return true;
        },
        [
            participants,
            setParticipants,
            breakoutRoomsNames,
            setBreakoutRoomsNames,
        ],
    );

    const duplicateRoomNameModal = useMemo(
        () => (
            <DuplicateRoomNameModal
                isOpen={showDuplicateRoomNameModal}
                onConfirm={() => setShowDuplicateRoomNameModal(false)}
            />
        ),
        [showDuplicateRoomNameModal],
    );

    return {handleBreakoutRoomNameChange, duplicateRoomNameModal};
};
